import { SeoLandingLayout, SeoLandingPageData } from '../../../components/SeoLandingLayout';

const data: SeoLandingPageData = {
  seo: {
    title: 'Trésorerie startup : gérer le cash en phase de croissance | Trezoone',
    description: 'La startup brûle du cash vite. Runway, burn rate, point mort… Voici comment piloter la trésorerie d\'une startup avec les bons indicateurs.',
    canonical: '/tresorerie-startup',
    keywords: 'trésorerie startup, burn rate startup, runway startup, gestion cash startup, trésorerie jeune entreprise',
  },
  hero: {
    tag: 'Trésorerie startup',
    title: 'Combien de mois reste-t-il avant la mort de votre startup ?',
    subtitle: 'Le runway, c\'est le temps que vous avez avant d\'être à court de cash. C\'est l\'indicateur de survie n°1 d\'une startup. Voici comment le calculer et le prolonger.',
    ctaLabel: 'Calculer mon runway',
    ctaHref: '/simulateur-tresorerie-association',
  },
  pilierLink: { label: 'Gestion trésorerie', href: '/gestion-tresorerie' },
  sections: [
    {
      type: 'explain',
      title: 'Les indicateurs clés de la trésorerie startup',
      content: 'Une startup pilote sa trésorerie avec 3 métriques essentielles :',
      items: [
        'Burn rate mensuel : combien vous dépensez par mois (charges fixes + variables)',
        'MRR (Monthly Recurring Revenue) : vos revenus récurrents mensuels',
        'Runway = Trésorerie disponible ÷ Burn rate net (burn - MRR)',
        'Exemple : 120 000 € en banque, burn 30 000 €/mois, MRR 10 000 € → runway = 120 000 / 20 000 = 6 mois',
      ],
    },
    {
      type: 'example',
      title: 'Piège classique : la startup qui ne voit pas venir la fin',
      content: 'Une startup SaaS lève 500 000 € en amorçage. Burn rate 40 000 €/mois. Runway initial 12,5 mois.',
      items: [
        'Mois 6 : recrutement de 2 développeurs → burn passe à 65 000 €/mois',
        'Mois 8 : MRR à 15 000 € (progression lente) → burn net 50 000 €',
        'Runway recalculé à M8 : 180 000 € / 50 000 € = 3,6 mois',
        'Alerte : les fondateurs ont 3,6 mois pour lever ou atteindre le breakeven',
        'Avec Trezoone : l\'alerte aurait pu arriver au mois 5, laissant 7 mois pour agir',
      ],
    },
    {
      type: 'midcta',
      title: 'Votre runway fond plus vite que prévu ? Recalculez-le maintenant avant d\'agir trop tard',
      content: 'Trezoone calcule votre burn rate réel et votre runway en temps réel. Simule l\'impact d\'un recrutement ou d\'un ralentissement MRR en quelques secondes.',
    },
    {
      type: 'solution',
      title: 'Trezoone : le tableau de bord de trésorerie startup',
      content: 'Suivez votre burn rate, votre runway et votre point mort en temps réel.',
      items: [
        'Calcul automatique du burn rate mensuel depuis vos transactions réelles',
        'Indicateur runway mis à jour en temps réel',
        'Projection du point mort (mois où MRR couvre le burn)',
        'Simulation de l\'impact d\'un recrutement ou d\'un nouvel investissement',
        'Rapport investisseur sur l\'évolution de la trésorerie',
      ],
    },
    {
      type: 'cluster',
      title: 'Piloter sa startup avec les bons indicateurs financiers',
      links: [
        { label: 'Gestion trésorerie', href: '/gestion-tresorerie' },
        { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
        { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
        { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
        { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
        { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
        { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
        { label: 'Différence trésorerie résultat', href: '/difference-tresorerie-resultat' },
      ],
    },
    {
      type: 'cta',
      title: 'Pilotez la trésorerie de votre startup en temps réel',
      content: 'Trezoone donne aux fondateurs la visibilité qu\'ils n\'ont pas d\'habitude. Essai gratuit 14 jours.',
    },
  ],
  relatedLinks: [
    { label: 'Gestion trésorerie', href: '/gestion-tresorerie' },
    { label: 'Stress test trésorerie', href: '/stress-test-tresorerie' },
    { label: 'Plan de trésorerie', href: '/plan-tresorerie' },
    { label: 'Manque de trésorerie', href: '/manque-tresorerie' },
    { label: 'Cash flow entreprise', href: '/cash-flow-entreprise' },
    { label: 'Prévision trésorerie', href: '/prevision-tresorerie' },
    { label: 'Alerte trésorerie', href: '/alerte-tresorerie' },
    { label: 'Différence trésorerie résultat', href: '/difference-tresorerie-resultat' },
  ],
};

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }
export default function TresorerieStartupPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return <SeoLandingLayout data={data} onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />;
}
